/**
 * RUN 90 — putting the Appointment Matrix Table on one sheet of A4.
 *
 * The Technical Delegate asked for the matrix as a PDF, landscape, everything on a single page.
 * No PDF writer is added for it. This system has exactly one way of putting anything on paper —
 * `window.print()` over an `@media print` block, which is how the hearing letters and the daily
 * debrief document already go out — and a second, client-side PDF library would be a second
 * renderer to keep honest: the day the matrix gained a column, the paper copy and the screen
 * copy would have to be made to agree twice. The browser's own print dialogue already offers
 * `Save as PDF`, so the press the TD wants and the file he wants are the same press.
 *
 * What is new here is the arithmetic the letters never needed. A hearing letter is a column of
 * prose that may run to a second sheet and nobody minds. This matrix may not: it is read as a
 * grid, and a grid cut down the middle by a page turn is not a grid. So the press measures the
 * table it is about to print and hands the stylesheet a scale factor that makes it fit.
 *
 * THREE DECISIONS WORTH ARGUING, because each of them is a place the obvious thing fails.
 *
 * 1. `zoom`, not `transform: scale()`. A transform paints the element smaller and leaves its
 *    LAYOUT BOX at full size, so the print engine still paginates against the unshrunken table
 *    and still spills onto a second sheet — the one thing this feature exists to prevent.
 *    `zoom` scales the layout itself, so the page box shrinks with the ink. It is what the
 *    fit depends on and it is why the scale is delivered as a custom property rather than as a
 *    transform.
 *
 * 2. The page size is injected for the duration of the press rather than written into the
 *    stylesheet. `editorial.css` carries an unnamed `@page { size: A4 portrait }` that every
 *    document in this system prints under; a second unnamed `@page` appended to that file would
 *    win by coming later and would silently turn the hearing letters landscape. A named page
 *    (`page: matrix`) would scope it properly but is not carried by every engine a TD may be
 *    sitting in front of at a tournament. A `<style>` element added before the dialogue and
 *    removed after it is scoped by construction, needs nothing of the engine, and cannot
 *    outlive the press.
 *
 * 3. The page is emptied by marking the printed panel's ancestors, not by trusting the existing
 *    `body * { visibility: hidden }` rule on its own. That rule hides the ink but leaves every
 *    other panel occupying its full height, and this screen is several thousand pixels tall —
 *    the matrix would come out followed by a dozen blank sheets. Marking the chain from the
 *    panel up to `body` lets one stylesheet rule take every element OFF that chain out of the
 *    layout, which also neutralises, in one stroke, the narrow-screen collapses that a print
 *    engine fires by mistake: width media queries are evaluated against the PAPER, and A4's
 *    printable width is under 1100 CSS px, so this page's `max-width: 1100px` and
 *    `max-width: 780px` rules are live on paper even though nothing is wrong on any screen.
 *    That is the bug that bit the hearing letterhead in RUN 88. The ancestors are flattened to
 *    plain blocks in the print block, so whichever of those collapses fires has nothing left to
 *    collapse.
 *
 * Nothing in here runs on screen, and nothing it touches survives the press: every class, custom
 * property and style element added on the way in is removed again by one idempotent teardown.
 */

/* The one press this system has ever had for putting a document on paper — `window.print()`,
   guarded for jsdom. Imported rather than copied, for the reason the debrief document page
   gives: there must be exactly one of it. */
import { printDocument } from "../hearings/documentText.js";

/** On `body` for the length of the press; every print rule in RUN 90 hangs off it. */
export const MATRIX_PRINT_BODY_CLASS = "is-matrix-print";

/** On the one panel being printed — the AI matrix and the Umpires Managers' matrix are two. */
export const MATRIX_PRINT_SHEET_CLASS = "matprint";

/** On each element between that panel and `body`, so the rest of the page can be lifted out. */
export const MATRIX_PRINT_ANCESTOR_CLASS = "matprint__anc";

/**
 * The printable area of A4 landscape inside 10mm margins — 277mm × 190mm — in CSS px at the
 * 96dpi a CSS px is defined against. 277 / 25.4 × 96 = 1047; 190 / 25.4 × 96 = 718.
 */
export const MATRIX_PRINT_WIDTH_PX = 1047;
export const MATRIX_PRINT_HEIGHT_PX = 718;

/**
 * The height the title, the filter line and the density legend take above and below the table.
 * A constant rather than a measurement because those three are `display: none` on screen and a
 * hidden element has no height to read; it is generous on purpose, since over-reserving costs a
 * little type size and under-reserving costs the page.
 */
const MATRIX_PRINT_CHROME_PX = 96;

/**
 * Held back from the exact fit. The measurement is taken from the screen's layout and the paper
 * re-lays it out under a different scale, where a rounded-up line height or a surname that wraps
 * one word sooner can add a few pixels. 5% is the margin for that re-flow.
 */
const MATRIX_PRINT_SAFETY = 0.95;

/**
 * The floor on the scale. It exists so a pathological table cannot be shrunk to a smudge, NOT as
 * a page-fit escape: at this tournament's shape — 25 columns by 35 rows — the fit lands near
 * 0.6, and at the worst case the specification contemplates — 30 by 40 — near 0.5, which puts
 * the matrix's 12px figures at roughly 4.5pt. That is small, and it is the conscious trade-off:
 * the TD asked for one page, and a grid read across a page turn is worth less than a grid read
 * with the reading glasses he already wears at the table. 0.2 is below anything a real panel of
 * umpires can produce, so in practice this bound is never the one that binds.
 */
const MATRIX_PRINT_FLOOR = 0.2;

/**
 * How long the teardown waits for `afterprint` before doing it anyway.
 *
 * `window.print()` blocks until the dialogue is dismissed in every engine this runs in, so the
 * timer is only armed once the press is already over and it can never strip the styling out from
 * under a print in progress. It is here for the engine that never fires `afterprint` at all,
 * where without it the page would stay in its print dress until the next reload.
 */
const MATRIX_PRINT_FALLBACK_MS = 1500;

/**
 * The scale that puts a table of `width` × `height` CSS px on one landscape sheet.
 *
 * Never above 1: a small matrix is printed at the size it is drawn on screen rather than blown
 * up to fill the paper, because the figures are meant to read as the same table the TD was just
 * looking at. Unmeasurable input (jsdom, a table not yet laid out) answers 1, which prints the
 * matrix at its natural size rather than at some guessed fraction of it.
 */
export function matrixPrintScale(width: number, height: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return 1;
  if (width <= 0 || height <= 0) return 1;
  const fit = Math.min(
    MATRIX_PRINT_WIDTH_PX / width,
    MATRIX_PRINT_HEIGHT_PX / (height + MATRIX_PRINT_CHROME_PX),
  );
  if (fit >= 1) return 1;
  return Math.max(MATRIX_PRINT_FLOOR, Math.round(fit * MATRIX_PRINT_SAFETY * 1000) / 1000);
}

/**
 * Print one matrix panel: measure it, dress the document for a single landscape sheet, open the
 * browser's dialogue, and undress it again.
 *
 * `sheet` is the panel — the competition filter and the On Field Only state are already in the
 * markup it holds, so what prints is what is on the screen and there is nothing here that could
 * print a different view. `table` is the grid inside it, measured rather than the panel itself
 * because the panel's head and foot mostly do not print and its wrapper clips the table's height
 * to the screen; `scrollWidth`/`scrollHeight` read through that clip to the full grid.
 */
export function printUmpireMatrix(sheet: HTMLElement | null, table: HTMLElement | null): void {
  if (sheet === null || typeof window === "undefined" || typeof document === "undefined") return;

  const width = table === null ? 0 : table.scrollWidth;
  const height = table === null ? 0 : table.scrollHeight;
  sheet.style.setProperty("--matprint-scale", String(matrixPrintScale(width, height)));
  /* The printed panel is held to the width the table measured, so the paper re-lays the columns
     exactly as the measurement found them. Left unset, a `width: 100%` table inside a block of
     unconstrained width would spread to its max-content width — wider than what was measured,
     and therefore wider than the scale was calculated for. */
  if (width > 0) sheet.style.setProperty("--matprint-w", `${String(width)}px`);

  sheet.classList.add(MATRIX_PRINT_SHEET_CLASS);
  const ancestors: HTMLElement[] = [];
  for (let node = sheet.parentElement; node !== null; node = node.parentElement) {
    node.classList.add(MATRIX_PRINT_ANCESTOR_CLASS);
    ancestors.push(node);
  }
  document.body.classList.add(MATRIX_PRINT_BODY_CLASS);

  const page = document.createElement("style");
  page.dataset.matrixPrint = "";
  page.textContent = "@page { size: A4 landscape; margin: 10mm; }";
  document.head.append(page);

  let timer = 0;
  let torn = false;
  const teardown = (): void => {
    if (torn) return;
    torn = true;
    window.removeEventListener("afterprint", teardown);
    window.clearTimeout(timer);
    page.remove();
    document.body.classList.remove(MATRIX_PRINT_BODY_CLASS);
    for (const node of ancestors) node.classList.remove(MATRIX_PRINT_ANCESTOR_CLASS);
    sheet.classList.remove(MATRIX_PRINT_SHEET_CLASS);
    sheet.style.removeProperty("--matprint-scale");
    sheet.style.removeProperty("--matprint-w");
  };

  window.addEventListener("afterprint", teardown);
  printDocument();
  if (!torn) timer = window.setTimeout(teardown, MATRIX_PRINT_FALLBACK_MS);
}
